import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import type { FeatureCollection, MultiPolygon } from 'geojson';
import { CropMapLocationCountryResponse } from '../../../proxy/proxies';
import { AppConfigService } from '../../services/app-config.service';
import { BoundaryDecoderService } from './boundary-decoder.service';

@Injectable({ providedIn: 'root' })
export class CityBoundaryLoaderService {
    private readonly http = inject(HttpClient);
    private readonly appConfigService = inject(AppConfigService);
    private readonly boundaryDecoder = inject(BoundaryDecoderService);

    private readonly cache = new Map<string, ArrayBuffer>();

    loadCityBoundariesForCountry(
        country: CropMapLocationCountryResponse | undefined,
    ): Observable<FeatureCollection<MultiPolygon>> {
        const regionKeys = this.collectCityBlobKeysForCountry(country);
        return this.loadCityBoundariesByRegionKeys(regionKeys);
    }

    /** Region/state access keys for `{path}/cities/{key}.bin` (API: RegionAccessKey). */
    collectCityBlobKeysForCountry(country: CropMapLocationCountryResponse | undefined): (string | number)[] {
        const keys: (string | number)[] = [];

        for (const region of country?.regions ?? []) {
            const key = region.accessKey?.trim() || region.regionId;
            if (key != null && key !== '') {
                keys.push(key);
            }
        }

        if (keys.length === 0) {
            for (const state of country?.states ?? []) {
                const key = state.accessKey?.trim() || state.stateId;
                if (key != null && key !== '') {
                    keys.push(key);
                }
            }
        }

        return keys;
    }

    loadCityBoundariesByRegionKeys(
        regionKeys: (string | number)[],
    ): Observable<FeatureCollection<MultiPolygon>> {
        const path = this.appConfigService.geographicBoundariesPath?.replace(/\/+$/, '');
        const empty = this.boundaryDecoder.mergeFeatureCollections([]);
        const keys = [...new Set(regionKeys.map((k) => String(k)).filter(Boolean))];

        if (!path || keys.length === 0) {
            return of(empty);
        }

        const fetches = keys.map((key) => {
            const cached = this.cache.get(key);
            const stream$ = cached
                ? of(cached)
                : this.http.get(`${path}/cities/${key}.bin`, { responseType: 'arraybuffer' });
            return stream$.pipe(
                map((buf) => ({ key, buf })),
                catchError((err) => {
                    console.warn(`[CityBoundaryLoader] city boundary fetch failed for ${key}`, err);
                    return of(null);
                }),
            );
        });

        return forkJoin(fetches).pipe(
            map((results) => {
                const collections = [];
                for (const item of results) {
                    if (!item) continue;
                    this.cache.set(item.key, item.buf);
                    collections.push(this.boundaryDecoder.decodeCityBoundaries(item.key, item.buf));
                }
                return this.boundaryDecoder.mergeFeatureCollections(collections);
            }),
        );
    }
}
